<script setup lang="ts">
/**
 * Authenticated cabinet shell: sidebar + topbar + scrollable main.
 *
 * Rendered by App.vue for routes with `meta.layout === 'app'`. The router guard
 * guarantees a session before any app-layout route resolves, so `auth.me` is
 * always present by the time this mounts.
 */
import { ref, onMounted, onUnmounted } from 'vue';
import AppSidebar from '../components/AppSidebar.vue';
import AppTopBar from '../components/AppTopBar.vue';

const drawerOpen = ref(false);

function handleKeydown(e: KeyboardEvent): void {
  if (e.key === 'Escape' && drawerOpen.value) drawerOpen.value = false;
}

onMounted(() => document.addEventListener('keydown', handleKeydown));
onUnmounted(() => document.removeEventListener('keydown', handleKeydown));
</script>

<template>
  <div class="min-h-screen grid grid-cols-[64px_1fr] lg:grid-cols-[260px_1fr] bg-canvas">
    <AppSidebar id="main-sidebar" :mobile-open="drawerOpen" @close="drawerOpen = false" />
    <!-- Mobile drawer backdrop -->
    <div
      v-if="drawerOpen"
      class="fixed inset-0 bg-black/40 z-40 lg:hidden"
      @click="drawerOpen = false"
      aria-hidden="true"
    />
    <div class="flex flex-col min-w-0">
      <AppTopBar :drawer-open="drawerOpen" @toggle-sidebar="drawerOpen = !drawerOpen" />
      <main class="flex-1 overflow-auto bg-canvas">
        <slot />
      </main>
    </div>
  </div>
</template>
